import { useRef } from 'react';
import type { PointerEvent as ReactPointerEvent, ReactNode } from 'react';
import type { PanelBounds, PanelGeometry, ResizeHandle } from '../obr/floatingPanel.ts';
import { resizePanelFromHandle } from '../obr/floatingPanel.ts';

export function FloatingPanelChrome({
  title,
  geometry,
  bounds,
  onLiveResize,
  onCommitGeometry,
  onMoveBy,
  onClose,
  children,
}: {
  title: string;
  geometry: PanelGeometry;
  bounds: PanelBounds;
  onLiveResize: (width: number, height: number) => void | Promise<void>;
  onCommitGeometry: (next: PanelGeometry) => void | Promise<void>;
  onMoveBy: (dx: number, dy: number) => void | Promise<void>;
  onClose?: () => void | Promise<void>;
  children: ReactNode;
}) {
  const titleDrag = useRef<{ pointerId: number; x: number; y: number } | null>(null);
  const resizeDrag = useRef<{
    pointerId: number;
    x: number;
    y: number;
    start: PanelGeometry;
    handle: ResizeHandle;
  } | null>(null);

  function titleDown(event: ReactPointerEvent<HTMLDivElement>) {
    if ((event.target as HTMLElement).closest('button')) return;
    titleDrag.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY };
    event.currentTarget.setPointerCapture(event.pointerId);
    event.preventDefault();
  }

  function titleUp(event: ReactPointerEvent<HTMLDivElement>) {
    const start = titleDrag.current;
    titleDrag.current = null;
    if (!start || start.pointerId !== event.pointerId) return;
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    if (Math.abs(dx) < 3 && Math.abs(dy) < 3) return;
    void onMoveBy(dx, dy);
  }

  function resizeDown(handle: ResizeHandle, event: ReactPointerEvent<HTMLDivElement>) {
    resizeDrag.current = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      start: geometry,
      handle,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    event.preventDefault();
    event.stopPropagation();
  }

  function resized(event: ReactPointerEvent<HTMLDivElement>): PanelGeometry | null {
    const start = resizeDrag.current;
    if (!start || start.pointerId !== event.pointerId) return null;
    return resizePanelFromHandle(
      start.start,
      start.handle,
      event.clientX - start.x,
      event.clientY - start.y,
      bounds,
    );
  }

  function resizeMove(event: ReactPointerEvent<HTMLDivElement>) {
    const next = resized(event);
    if (!next) return;
    void onLiveResize(next.width, next.height);
  }

  function resizeUp(event: ReactPointerEvent<HTMLDivElement>) {
    const next = resized(event);
    resizeDrag.current = null;
    if (!next) return;
    void onCommitGeometry(next);
  }

  const handles: ResizeHandle[] = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];

  return (
    <div className="dcc-window-shell">
      <div
        className="dcc-window-titlebar"
        onPointerDown={titleDown}
        onPointerUp={titleUp}
        title="Drag, then release, to move this window"
      >
        <strong>{title}</strong>
        <span className="dcc-window-size">{geometry.width}×{geometry.height}</span>
        {onClose && (
          <button type="button" className="dcc-window-close" onPointerDown={(event) => event.stopPropagation()} onClick={() => void onClose()} title="Close">×</button>
        )}
      </div>
      <div className="dcc-window-content">{children}</div>
      {handles.map((handle) => (
        <div
          key={handle}
          className={`dcc-resize-handle dcc-resize-${handle}`}
          aria-hidden="true"
          onPointerDown={(event) => resizeDown(handle, event)}
          onPointerMove={resizeMove}
          onPointerUp={resizeUp}
          onPointerCancel={() => { resizeDrag.current = null; }}
        />
      ))}
    </div>
  );
}
